import _ from 'lodash'
import moment from 'moment'
import config from '../config'
import { cli } from '../config/init'
import {
	BoolMachine,
	BoolMachineCategory,
	Control,
	Log,
	Machine,
	MachineCategory,
	Sensor,
	StepMachine,
	StepRate,
	Trigger,
} from '../types'
import { specialConfig } from '../hooks/useLocalConfig'

export function sleep(msec: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, msec))
}

export const SENSOR = {
	TMP: 1,
	HMD: 2,
	CO2: 3,
	PPM: 4,
	PSR: 5,
	WAT: 6,
	LUX: 7,
	TMP2: 8,
	TMP3: 9,
}
export const sensorLib: { [n: number]: Sensor } = {
	1: {
		id: 1,
		label: '気温',
		unit: '℃',
		key: 'tmp',
	},
	2: {
		id: 2,
		label: '湿度',
		unit: '%',
		key: 'hmd',
	},
	3: {
		id: 3,
		label: 'CO2',
		unit: 'ppm',
		key: 'co2',
	},
	4: {
		id: 4,
		label: '気圧',
		unit: 'hPa',
		key: 'psr',
	},
	5: {
		id: 5,
		label: '雨',
		unit: '',
		key: 'wat',
	},
	6: {
		id: 6,
		label: '日射量',
		unit: 'W',
		key: 'lux',
	},
	8: {
		id: 8,
		label: '温度2',
		unit: '℃',
		key: 'tmp2',
	},
	9: {
		id: 9,
		label: '温度3',
		unit: '℃',
		key: 'tmp3',
	},
}

export const sensorLibByKey: { [key: string]: Sensor } = _.keyBy(
	Object.values(sensorLib),
	'key',
)

// Get sensor library with house-specific labels
export function getSensorLib(houseId?: number): { [n: number]: Sensor } {
	if (!houseId || !specialConfig[houseId]) {
		return sensorLib
	}

	const config = specialConfig[houseId]
	const customLib = { ...sensorLib }

	// Apply custom labels for this house
	if (config.tmp && customLib[1]) {
		customLib[1] = { ...customLib[1], label: config.tmp }
	}
	if (config.tmp2 && customLib[8]) {
		customLib[8] = { ...customLib[8], label: config.tmp2 }
	}
	if (config.tmp3 && customLib[9]) {
		customLib[9] = { ...customLib[9], label: config.tmp3 }
	}

	return customLib
}

export const graphSensorLabelByKey: Record<string, string> = {
	tmp: sensorLib[1].label,
	hmd: sensorLib[2].label,
	co2: sensorLib[3].label,
	psr: sensorLib[4].label,
	wat: sensorLib[5].label,
	lux: sensorLib[6].label,
	tmp2: sensorLib[8].label,
	tmp3: sensorLib[9].label,
	vpd: '飽差',
	dew: '露点',
	soilHmd: '土中湿度',
}

export function makeDayLabel(daystring?: string): string {
	return moment(daystring).format('YYYY/MM/DD')
}

// sensorLib に無いセンサー番号（土壌系など）の表示用ラベル・単位。
// backend の Trigger#sensor_str に準拠（13=土壌温度, 14=土壌湿度 ほか）。
const extraSensorInfo: { [n: number]: { label: string; unit: string } } = {
	13: { label: '土壌温度', unit: '℃' },
	14: { label: '土壌湿度', unit: '%' },
}

// 未知のセンサー番号でもクラッシュしないラベル/単位解決
const sensorInfo = (
	sensorNo: number,
): { label: string; unit: string; key?: string } => {
	const s = sensorLib[sensorNo]
	if (s) return s
	const e = extraSensorInfo[sensorNo]
	if (e) return e
	return { label: `センサー${sensorNo}`, unit: '' }
}

export function triggerToLabel(trigger: Trigger, houseId?: number): string {
	const sensor = sensorInfo(trigger.sensor)

	// Use house-specific label if available
	if (houseId && specialConfig[houseId]) {
		const config = specialConfig[houseId]
		if (sensor.key === 'tmp' && config.tmp) return config.tmp
		if (sensor.key === 'tmp2' && config.tmp2) return config.tmp2
		if (sensor.key === 'tmp3' && config.tmp3) return config.tmp3
	}

	return sensor.label
}

export function triggerToRuleText(trigger: Trigger): string {
	const { unit } = sensorInfo(trigger.sensor)
	const { low, high } = trigger

	if (low && high) {
		return `${low}${unit}〜${high}${unit}`
	} else if (low) {
		return `${low}${unit}〜`
	} else if (high) {
		return `〜${high}${unit}`
	}
	return ``
}

export const isBoolMachineCategory = (
	key: MachineCategory | null,
): key is BoolMachineCategory =>
	['fan', 'heating', 'mist', 'co2', null].includes(key)
export const isStepMachineCategory = (key: MachineCategory | null) =>
	!isBoolMachineCategory(key) &&
	key !== 'hp' &&
	key !== 'tway' &&
	key !== 'denji'
export const isHpMachineCategory = (key: MachineCategory | null) => key === 'hp'
export const isTwayMachineCategory = (key: MachineCategory | null) =>
	key === 'tway'
export const isDenjiMachineCategory = (key: MachineCategory | null) =>
	key === 'denji'
export function isBoolMachine(machine: Machine): machine is BoolMachine {
	return isBoolMachineCategory(machine.category)
}

export function isStepControl(machine: Machine): machine is StepMachine {
	return !isBoolMachine(machine)
}

export const categories: MachineCategory[] = [
	'curtain',
	'window',
	'fan',
	'mist',
	'heating',
	'co2',
	'hp',
	'tway',
	'denji',
]
export function isMachineCategory(s: string): s is MachineCategory {
	return s in categories
}

export const categoryStr: Record<MachineCategory, string> = {
	curtain: 'カーテン',
	window: '窓',
	fan: 'ファン',
	mist: 'ミスト',
	heating: 'ヒーター',
	co2: 'CO2',
	hp: 'HP',
	tway: '三方弁',
	denji: '電磁弁',
	none: '未設定',
}

export const compTrigger = (trigger: Trigger) => [
	trigger.sensor,
	trigger.low,
	trigger.high,
]

export const compControl = (control: Control) => [
	categories.indexOf(control.category),
	control.label,
]

function isStepRate(sr: number): sr is StepRate {
	return sr in [0, 1, 2, 3, 4, 5, 6]
}

export const toStepRate = (rate: number) => {
	const sr = Math.ceil((rate * config.stepCount) / 100)

	return isStepRate(sr) ? sr : 0
}

export const toj = (a: object) => JSON.stringify(a)

export function download(text: string, name: string) {
	const bom = '\uFEFF'
	const href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(bom + text)
	const a = document.createElement('a')

	a.setAttribute('href', href)
	a.setAttribute('download', name + '.csv')
	if (document.body) document.body.appendChild(a)
	a.click()
	a.remove()
}

// Japanese header mappings
const japaneseHeaders: Record<string, string> = {
	timestamp: 'タイムスタンプ',
	timestampStr: '日時',
	hourId: 'hour_id',
	minutesId: 'minutes_id',
	tmp: '気温',
	hmd: '湿度',
	co2: 'CO2',
	psr: '気圧',
	wat: '雨',
	lux: '日射量',
	tmp2: '温度2',
	tmp3: '温度3',
	tmpB1: '温度B1',
	tmpB2: '温度B2',
	tmpB3: '温度B3',
	tmpB4: '温度B4',
	soilTmp: '土壌温度',
	soilHmd: '土壌湿度',
	soilPh: '土壌pH',
	soilEc: '土壌EC',
	hp1Err: 'HP1エラー',
	hp1Mode: 'HP1モード',
	hp2Err: 'HP2エラー',
	hp2Mode: 'HP2モード',
	tway1: '三方弁1',
	tway2: '三方弁2',
	bad: '不良',
	vpd: '飽差',
	dew: '露点',
}

function filterFirstLogPerMinute(logs: Log[]): Log[] {
	const sortedLogs = _.sortBy(logs, 'timestamp')

	return _.uniqBy(sortedLogs, (log) =>
		moment(log.timestamp).format('YYYY-MM-DD HH:mm'),
	)
}

export type CsvExportInterval = 'minute' | 'tenMinutes' | 'hour'
type CsvExportIntervalOption = CsvExportInterval | boolean
type CsvDateRange = { startDate: string; endDate: string }

const TEN_MINUTE_INTERVAL = 10
const CSV_AVERAGE_FIELD_KEYS = [
	'tmp',
	'hmd',
	'co2',
	'psr',
	'wat',
	'lux',
	'tmp2',
	'tmp3',
	'tmpB1',
	'tmpB2',
	'tmpB3',
	'tmpB4',
	'soilTmp',
	'soilHmd',
	'soilPh',
	'soilEc',
	'hp1Err',
	'hp1Mode',
	'hp2Err',
	'hp2Mode',
	'tway1',
	'tway2',
	'bad',
	'vpd',
	'dew',
] as const
type CsvAverageFieldKey = typeof CSV_AVERAGE_FIELD_KEYS[number]
type CsvLog = Partial<
	Omit<Log, CsvAverageFieldKey | 'timestamp'> &
		Record<CsvAverageFieldKey, number | boolean | null>
> & {
	timestamp: number
	hourId?: string
}

function resolveCsvExportInterval(
	exportInterval: CsvExportIntervalOption,
): CsvExportInterval {
	if (typeof exportInterval === 'boolean') {
		return exportInterval ? 'hour' : 'minute'
	}

	return exportInterval
}

function getTenMinuteBucketTimestamp(timestamp: number): number {
	const bucketTimestamp = moment(timestamp).startOf('minute')
	const bucketMinute =
		Math.floor(bucketTimestamp.minute() / TEN_MINUTE_INTERVAL) *
		TEN_MINUTE_INTERVAL

	return bucketTimestamp.minute(bucketMinute).valueOf()
}

function getAverageValue(
	logs: Log[],
	fieldKey: CsvAverageFieldKey,
): number | null {
	const values = logs
		.map((log) => log[fieldKey])
		.flatMap((value) => {
			if (typeof value === 'boolean') return [Number(value)]
			if (typeof value === 'number' && Number.isFinite(value)) return [value]
			return []
		})

	return values.length > 0 ? _.sum(values) / values.length : null
}

function averageLogsInBucket(logs: Log[], bucketTimestamp: number): CsvLog {
	const firstLog = logs[0]
	const averagedFields = CSV_AVERAGE_FIELD_KEYS.reduce<
		Partial<Record<CsvAverageFieldKey, number | null>>
	>(
		(fields, fieldKey) => ({
			...fields,
			[fieldKey]: getAverageValue(logs, fieldKey),
		}),
		{},
	)

	return {
		...firstLog,
		...averagedFields,
		timestamp: bucketTimestamp,
		createdAt: new Date(bucketTimestamp).toISOString(),
	}
}

function getTenMinuteBucketTimestamps(
	logs: Log[],
	dateRange?: CsvDateRange,
): number[] {
	const sortedLogs = _.sortBy(logs, 'timestamp')
	const firstLog = _.first(sortedLogs)
	const lastLog = _.last(sortedLogs)
	const firstBucketTimestamp = dateRange
		? moment(dateRange.startDate).startOf('day').valueOf()
		: firstLog
		? getTenMinuteBucketTimestamp(firstLog.timestamp)
		: undefined
	const lastBucketTimestamp = dateRange
		? moment(dateRange.endDate).endOf('day').valueOf()
		: lastLog
		? getTenMinuteBucketTimestamp(lastLog.timestamp)
		: undefined

	if (firstBucketTimestamp === undefined || lastBucketTimestamp === undefined) {
		return []
	}

	// 期間指定時は欠測枠も比較できるよう、各日の00:00から23:50まで生成する。
	const bucketCount =
		Math.floor(
			moment(lastBucketTimestamp).diff(
				moment(firstBucketTimestamp),
				'minutes',
			) / TEN_MINUTE_INTERVAL,
		) + 1

	return _.range(bucketCount).map((bucketIndex) =>
		moment(firstBucketTimestamp)
			.add(bucketIndex * TEN_MINUTE_INTERVAL, 'minutes')
			.valueOf(),
	)
}

function getCsvLogs(
	logs: Log[],
	exportInterval: CsvExportInterval,
	dateRange?: CsvDateRange,
): CsvLog[] {
	if (exportInterval !== 'tenMinutes') {
		return filterFirstLogPerMinute(logs)
	}

	const logsByBucket = _.groupBy(_.sortBy(logs, 'timestamp'), (log) =>
		String(getTenMinuteBucketTimestamp(log.timestamp)),
	)

	return getTenMinuteBucketTimestamps(logs, dateRange).map(
		(bucketTimestamp) => {
			const bucketLogs = logsByBucket[String(bucketTimestamp)] || []

			return bucketLogs.length > 0
				? averageLogsInBucket(bucketLogs, bucketTimestamp)
				: { timestamp: bucketTimestamp }
		},
	)
}

function getCsvTimestamp(
	timestamp: number,
	exportInterval: CsvExportInterval,
): number {
	if (exportInterval !== 'tenMinutes') {
		return timestamp
	}

	return getTenMinuteBucketTimestamp(timestamp)
}

export function makeCSV(
	logs: Log[],
	houseId?: number,
	exportIntervalOption: CsvExportIntervalOption = 'minute',
	dateRange?: CsvDateRange,
): string {
	const exportInterval = resolveCsvExportInterval(exportIntervalOption)
	const csvLogs = getCsvLogs(logs, exportInterval, dateRange)

	if (csvLogs.length === 0) {
		return 'noLog'
	}
	const fieldKeys: (keyof CsvLog)[] = ['tmp', 'hmd', 'co2', 'dew', 'lux', 'psr']

	const config = houseId ? specialConfig[houseId] : undefined
	const useJapanese = config?.jpHeader === true

	const isHourly = exportInterval === 'hour'
	const idKey = isHourly ? 'hourId' : 'minutesId'
	const headerKeys = [idKey, 'timestamp', 'timestampStr', ...fieldKeys]
	const head = useJapanese
		? headerKeys.map((key) => {
				if (key === 'tmp' && config?.tmp) return config.tmp
				if (key === 'tmp2' && config?.tmp2) return config.tmp2
				if (key === 'tmp3' && config?.tmp3) return config.tmp3
				return japaneseHeaders[key] || key
		  })
		: headerKeys

	const toLine = (log: CsvLog) => {
		const csvTimestamp = getCsvTimestamp(log.timestamp, exportInterval)
		const idValue = isHourly
			? log.hourId || ''
			: moment(csvTimestamp).format('YYYY-MM-DD HH:mm')
		return [
			idValue,
			csvTimestamp,
			moment(csvTimestamp).format('YYYY-MM-DD HH:mm:ss'),
			...fieldKeys.map((k) => (log[k] === undefined ? '' : log[k])),
		].map(String)
	}
	const cells = [head, ...csvLogs.map(toLine)]

	return cells.map((vs) => vs.join(',')).join('\n')
}

export function makeFullCSV(
	logs: Log[],
	houseId?: number,
	exportIntervalOption: CsvExportIntervalOption = 'minute',
	dateRange?: CsvDateRange,
): string {
	const exportInterval = resolveCsvExportInterval(exportIntervalOption)
	const csvLogs = getCsvLogs(logs, exportInterval, dateRange)

	if (csvLogs.length === 0) {
		return 'noLog'
	}
	const fieldKeys: (keyof CsvLog)[] = [
		'tmp',
		'hmd',
		'co2',
		'psr',
		'wat',
		'lux',
		'tmp2',
		'tmp3',
		'tmpB1',
		'tmpB2',
		'tmpB3',
		'tmpB4',
		'soilTmp',
		'soilHmd',
		'soilPh',
		'soilEc',
		'hp1Err',
		'hp1Mode',
		'hp2Err',
		'hp2Mode',
		'tway1',
		'tway2',
		'bad',
		'vpd',
		'dew',
	]

	const config = houseId ? specialConfig[houseId] : undefined
	const useJapanese = config?.jpHeader === true

	const isHourly = exportInterval === 'hour'
	const idKey = isHourly ? 'hourId' : 'minutesId'
	const headerKeys = [idKey, 'timestamp', 'timestampStr', ...fieldKeys]
	const head = useJapanese
		? headerKeys.map((key) => {
				if (key === 'tmp' && config?.tmp) return config.tmp
				if (key === 'tmp2' && config?.tmp2) return config.tmp2
				if (key === 'tmp3' && config?.tmp3) return config.tmp3
				return japaneseHeaders[key] || key
		  })
		: headerKeys

	const toLine = (log: CsvLog) => {
		const csvTimestamp = getCsvTimestamp(log.timestamp, exportInterval)
		const idValue = isHourly
			? log.hourId || ''
			: moment(csvTimestamp).format('YYYY-MM-DD HH:mm')
		return [
			idValue,
			csvTimestamp,
			moment(csvTimestamp).format('YYYY-MM-DD HH:mm:ss'),
			...fieldKeys.map((k) =>
				log[k] === undefined || log[k] === null ? '' : log[k],
			),
		].map(String)
	}
	const cells = [head, ...csvLogs.map(toLine)]

	return cells.map((vs) => vs.join(',')).join('\n')
}

export function downloadCSV(
	name: string,
	day: string,
	logs: Log[],
	houseId?: number,
) {
	download(makeCSV(logs, houseId), `pocketfarm_${name}_${day}`)
}

export function downloadFullCSV(
	name: string,
	day: string,
	logs: Log[],
	houseId?: number,
) {
	download(makeFullCSV(logs, houseId), `pocketfarm_${name}_${day}_full`)
}

function processLog(log: Log): Log {
	const timestamp = new Date(log.createdAt).getTime()
	return {
		...log,
		timestamp,
	}
}

async function fetchLogsForDay(houseId: number, day: string): Promise<Log[]> {
	const res = await cli().get<Log[]>(`/houses/${houseId}/logs`, {
		params: { day },
	})
	if (res.status !== 200) {
		throw new Error('Failed to fetch logs')
	}
	return res.data.map(processLog)
}

function getDateRange(startDate: string, endDate: string): string[] {
	const dates: string[] = []
	const start = moment(startDate)
	const end = moment(endDate)
	while (start.isSameOrBefore(end)) {
		dates.push(start.format('YYYY-MM-DD'))
		start.add(1, 'day')
	}
	return dates
}

function filterFirstLogPerHour(logs: Log[]): Log[] {
	const hourlyLogs = new Map<string, Log & { hourId: string }>()

	for (const log of logs) {
		const hourKey = moment(log.timestamp).format('YYYY-MM-DD HH')
		if (!hourlyLogs.has(hourKey)) {
			hourlyLogs.set(hourKey, { ...log, hourId: hourKey })
		}
	}

	return Array.from(hourlyLogs.values()).sort(
		(a, b) => a.timestamp - b.timestamp,
	)
}

export async function downloadRangeCSV(
	houseId: number,
	name: string,
	startDate: string,
	endDate: string,
	exportIntervalOption: CsvExportIntervalOption = 'minute',
) {
	const dates = getDateRange(startDate, endDate)
	let allLogs: Log[] = []

	for (const day of dates) {
		const logs = await fetchLogsForDay(houseId, day)
		allLogs.push(...logs)
	}

	const effectiveExportInterval = isAllLogEnableHouse(houseId)
		? resolveCsvExportInterval(exportIntervalOption)
		: 'minute'

	if (effectiveExportInterval === 'hour') {
		allLogs = filterFirstLogPerHour(allLogs)
	}

	const suffix =
		effectiveExportInterval === 'hour'
			? '_hourly'
			: effectiveExportInterval === 'tenMinutes'
			? '_10min'
			: ''
	const fileName = `pocketfarm_${name}_${startDate}_${endDate}${suffix}`
	download(
		makeCSV(allLogs, houseId, effectiveExportInterval, {
			startDate,
			endDate,
		}),
		fileName,
	)
}

export async function downloadRangeFullCSV(
	houseId: number,
	name: string,
	startDate: string,
	endDate: string,
	exportIntervalOption: CsvExportIntervalOption = 'minute',
) {
	const dates = getDateRange(startDate, endDate)
	let allLogs: Log[] = []

	for (const day of dates) {
		const logs = await fetchLogsForDay(houseId, day)
		allLogs.push(...logs)
	}

	const effectiveExportInterval = isAllLogEnableHouse(houseId)
		? resolveCsvExportInterval(exportIntervalOption)
		: 'minute'

	if (effectiveExportInterval === 'hour') {
		allLogs = filterFirstLogPerHour(allLogs)
	}

	const suffix =
		effectiveExportInterval === 'hour'
			? '_hourly'
			: effectiveExportInterval === 'tenMinutes'
			? '_10min'
			: ''
	const fileName = `pocketfarm_${name}_${startDate}_${endDate}_full${suffix}`
	download(
		makeFullCSV(allLogs, houseId, effectiveExportInterval, {
			startDate,
			endDate,
		}),
		fileName,
	)
}

const { NODE_ENV } = process.env

export const isDev = NODE_ENV === 'development'

/*
 * 16: d0006
 * 17: d0007
 * 3: debug
 * /

/**
 * Check if the house is a proto house (house ID 16 or 17)
 * Proto houses have additional sensor data (tmp2, tmp3) available
 */
export function isProtoHouse(houseId: number): boolean {
	const protoHouseIds = [16, 3] // d0006, debug
	return protoHouseIds.includes(houseId)
}

// 電磁弁 (denji) UI を表示するハウス許可リスト。
// ひとまず対象は proto ハウスと同じ（将来ここで独自リストに分岐できる）。
export function isDenjiEnabledHouse(houseId?: number): boolean {
	if (houseId === undefined) return false
	return isProtoHouse(houseId)
}
export function isAllLogEnableHouse(houseId: number): boolean {
	const allLogEnableHouseIds = [16, 17, 3]
	return allLogEnableHouseIds.includes(houseId)
}

// 土中湿度(soilHmd)をグラフに表示するハウス許可リスト。
// ひとまず対象は proto ハウスと同じ（将来ここで独自リストに分岐できる）。
export function isSoilHmdEnabledHouse(houseId: number): boolean {
	const soilHmdEnabledHouseIds = [16, 3]
	return soilHmdEnabledHouseIds.includes(houseId)
}
